import React, { useEffect, useState } from 'react'
import { Link, useHistory, useLocation } from 'react-router-dom'
import { Box, Group, MultiSelect, Loader, Center, Chip, Grid } from '@mantine/core'
import { AppBadge, AppSelect, AppTabs } from '../../app/common/ui'
import { AppButton, AppText } from '../../app/common/ui'
import { format, formatDistanceToNow } from 'date-fns'
import { toast } from 'react-toastify'
import agent from '../../app/api/agent'
import { useStore } from '../../app/stores/store'
import { ReloaderRecipeDto } from '../../app/models/reloaderRecipe'
import { AmmoScoreBadge } from '../../app/common/ui'
import { CartridgeSpecDto } from '../../app/models/ammo'

const INTENDED_USES = [
  { value: 'target', label: 'Target' },
  { value: 'hunting', label: 'Hunting' },
  { value: 'defense', label: 'Defense' },
  { value: 'plinking', label: 'Plinking' }
]

export default function ReloaderForum () {
  const history = useHistory()
  const location = useLocation()
  const { userStore } = useStore()
  const [recipes, setRecipes] = useState<ReloaderRecipeDto[]>([])
  const [cartridgeSpecs, setCartridgeSpecs] = useState<CartridgeSpecDto[]>([])
  const [loading, setLoading] = useState(false)
  const [cartridgeId, setCartridgeId] = useState<string | null>(() => {
    const params = new URLSearchParams(location.search)
    return params.get('cartridgeSpecId')
  })
  const [powderType, setPowderType] = useState('')
  const [intendedUse, setIntendedUse] = useState<string | null>(null)
  const [sortBy, setSortBy] = useState('validated')
  const [showFlagged, setShowFlagged] = useState(false)

  const loadRecipes = async () => {
    setLoading(true)
    try {
      const result = await agent.ReloaderRecipes.list({
        cartridgeSpecId: cartridgeId || undefined,
        powderType: powderType || undefined,
        intendedUse: intendedUse || undefined,
        isFlagged: showFlagged ? true : undefined,
        pageSize: 50
      })
      setRecipes(result.items)
    } catch (error) {
      toast.error('Failed to load recipes')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    agent.CartridgeSpecs.list({ pageSize: 200 })
      .then((r) => setCartridgeSpecs(r.items))
      .catch(() => {})
  }, [])

  useEffect(() => {
    loadRecipes()
  }, [cartridgeId, powderType, intendedUse, sortBy, showFlagged])

  const canPost = userStore.user && userStore.user.ammoScore >= 50

  return (
    <Box sx={{ fontFamily: 'DM Sans, sans-serif', maxWidth: 900, margin: '0 auto', padding: 'var(--gs-lg)' }}>
      {/* Header */}
      <Box mb="lg">
        <AppText sx={{ fontFamily: 'Oswald, sans-serif', fontSize: 28, fontWeight: 600, marginBottom: 'var(--gs-xs)' }}>
          RELOADER FORUM
        </AppText>
        <AppText size="sm" color="dimmed">Share and test reloading recipes with the community</AppText>
      </Box>

      {/* Post Recipe Button */}
      <Box mb="lg">
        <AppButton
          component={Link}
          to="/ammo/reloading/new"
          color="ember"
          disabled={!canPost}
          title={!canPost ? `Requires 50 AmmoScore (yours: ${userStore.user?.ammoScore ?? 0})` : ''}
        >
          + Post a Recipe
        </AppButton>
      </Box>

      {/* Filters */}
      <Box mb="lg" sx={{ background: 'var(--gs-surface)', padding: 'var(--gs-md)', borderRadius: 6 }}>
        <Grid gutter="sm" mb="sm">
          <Grid.Col span={{ base: 12, sm: 6 }}>
            <AppSelect
              label="Cartridge"
              placeholder="All cartridges"
              searchable
              clearable
              value={cartridgeId}
              onChange={setCartridgeId}
              data={[
                { value: '', label: 'All Calibers' },
                ...cartridgeSpecs.map((s) => ({ value: s.id, label: s.name }))
              ]}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 6 }}>
            <AppSelect
              label="Powder Type"
              placeholder="All powders"
              searchable
              value={powderType}
              onChange={setPowderType}
              data={[]}
            />
          </Grid.Col>
        </Grid>

        <Group mb="sm">
          <AppText size="sm" weight={500}>Intended Use:</AppText>
          {['target', 'hunting', 'defense', 'plinking'].map((use) => (
            <Chip
              key={use}
              variant={intendedUse === use ? 'filled' : 'light'}
              onClick={() => setIntendedUse(intendedUse === use ? null : use)}
            >
              {INTENDED_USES.find(u => u.value === use)?.label}
            </Chip>
          ))}
        </Group>

        <Group>
          <AppSelect
            label="Sort"
            value={sortBy}
            onChange={(v) => setSortBy(v || 'validated')}
            data={[
              { value: 'validated', label: 'Most Validated' },
              { value: 'newest', label: 'Newest' }
            ]}
            w={200}
          />
          {userStore.user?.isModerator && (
            <Chip
              variant={showFlagged ? 'filled' : 'light'}
              onClick={() => setShowFlagged(!showFlagged)}
            >
              Show Flagged
            </Chip>
          )}
        </Group>
      </Box>

      {/* Recipe Cards */}
      {loading
        ? (
        <Center py="xl">
          <Loader />
        </Center>
          )
        : recipes.length === 0
          ? (
        <Box sx={{ textAlign: 'center', padding: 'var(--gs-lg)' }}>
          <AppText color="dimmed">No recipes found. Be the first to post one!</AppText>
        </Box>
            )
          : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {recipes.map((recipe) => (
            <RecipeCard key={recipe.id} recipe={recipe} onClick={() => history.push(`/ammo/reloading/${recipe.id}`)} />
          ))}
        </Box>
            )}
    </Box>
  )
}

function RecipeCard ({ recipe, onClick }: { recipe: ReloaderRecipeDto; onClick: () => void }) {
  return (
    <Box
      onClick={onClick}
      sx={{
        padding: 'var(--gs-md)',
        border: '1px solid var(--gs-border)',
        borderRadius: 6,
        background: 'var(--gs-card)',
        cursor: 'pointer',
        transition: 'border-color 0.15s',
        '&:hover': { borderColor: 'var(--gs-accent)' }
      }}
    >
      {recipe.isFlagged && (
        <Box sx={{ background: '#ffa94d', padding: 'var(--gs-xs)', borderRadius: 4, marginBottom: 'var(--gs-sm)' }}>
          <AppText size="xs" weight={600} sx={{ color: '#333' }}>
            ⚠️ Safety flagged — review before use
          </AppText>
        </Box>
      )}

      <Group position="apart" align="flex-start" mb="xs">
        <Box sx={{ flex: 1 }}>
          <AppText component={Link} to={`/ammo/specs/${recipe.cartridgeSpecId}`} weight={600} sx={{ color: 'var(--gs-text)' }}>
            {recipe.cartridgeSpecName}
          </AppText>
          <AppText size="sm" sx={{ fontFamily: 'JetBrains Mono, monospace', marginTop: 'var(--gs-xs)', marginBottom: 'var(--gs-xs)' }}>
            <strong>{recipe.powderBrand} {recipe.powderType}</strong> {recipe.chargeWeightGrMin}-{recipe.chargeWeightGrMax}gr
          </AppText>
          <AppText size="sm" color="dimmed">
            {recipe.bulletWeightGr}gr {recipe.bulletType} — {recipe.bulletBrand}
          </AppText>
          <AppText size="xs" color="dimmed">
            Primer: {recipe.primerBrand} {recipe.primerType} | OAL: {recipe.oalIn}"
          </AppText>
          {recipe.estimatedVelocityFps && (
            <AppText size="xs" sx={{ fontFamily: 'JetBrains Mono, monospace', marginTop: 'var(--gs-xs)' }}>
              ~{recipe.estimatedVelocityFps} fps
            </AppText>
          )}
        </Box>
        <Group position="right" sx={{ flexShrink: 0 }}>
          <AppBadge size="lg" color="green" variant="filled">
            {recipe.validationCount} Validated
          </AppBadge>
        </Group>
      </Group>

      <Group spacing="xs" mt="sm" position="apart">
        <Group spacing="xs">
          <AppBadge size="sm" variant="outline">
            {recipe.intendedUse}
          </AppBadge>
        </Group>
        <Group spacing="xs">
          <AppText component={Link} to={`/profiles/${recipe.authorUsername}`} size="xs" weight={500} sx={{ color: 'var(--gs-accent)' }}>
            {recipe.authorDisplayName || recipe.authorUsername}
          </AppText>
          <AmmoScoreBadge tier={recipe.authorAmmoTier} score={recipe.authorAmmoScore} size="xs" />
          <AppText size="xs" color="dimmed">
            {formatDistanceToNow(new Date(recipe.createdAt), { addSuffix: true })}
          </AppText>
        </Group>
      </Group>
    </Box>
  )
}
